import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import CloseButton from '@/components/UI/CloseButton/CloseButton';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import Resizer from '@/components/UI/Resizer/Resizer';
import StepsComponent from '@/components/UI/StepsComponent/StepsComponent';
import { WF_STATUS_WIDTH } from '@/helpers/constants';
import { addCollapsedDataWorkflow } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useError from '@/hooks/useError';
import { getCampaignInfo, getCampaignRecipients } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setResizerWF } from '@/redux/User/user.action';
import { getUserAll, getWFResizer, getSettingsInfo } from '@/redux/User/user.reducer';
import { setCampaignWorkflow, resetWorkflow } from '@/redux/Workflow/workflow.action';
import { getWorkflow } from '@/redux/Workflow/workflow.reducer';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, Workflow, Recipients, UserCompleteInfo, RouterParams, PlanStatus } from '@/types';
import styles from './CampaignStatus.module.scss';

type CampaignStatusProps = {
  timeZone: string;
  resizerWidth: number;
  setResizer: (value: number, save?: boolean) => void;
  resetWF: () => void;
  setWorkflow: (workflow: Workflow) => void;
  workflow: Workflow;
  recipients: Recipients;
  isSubCR: boolean;
  userData: UserCompleteInfo;
  campaignInfo: CampaignInfo;
  headerLanguage: string;
  subCRStatus: string;
};

const CampaignStatus: React.FC<CampaignStatusProps> = (props) => {
  const { timeZone, resetWF, isSubCR, userData, campaignInfo, headerLanguage, resizerWidth, setResizer, setWorkflow, workflow,
    recipients = {} as Recipients, subCRStatus } = props;
  const { t } = useTranslation();
  const { handleCriticalError } = useError();
  const lang = useAppLanguage();
  const { id, version } = useParams<RouterParams>();
  const [width, setWidth] = useState(resizerWidth);
  const [subStatus, setSubStatus] = useState<PlanStatus[] | null>(null);
  const [showExtendedStatus, setShowExtendedStatus] = useState(false);

  const refreshProgressBarDependencies = () => {
    const { causerApprover, informerApprover, causerPreApprover, informerPreApprover, causerCheckApprover,
      informerCheckApprover } = recipients || {};

    return [campaignInfo.status, lang, timeZone, version, causerApprover, causerPreApprover, causerCheckApprover,
      informerApprover, informerPreApprover, informerCheckApprover, subCRStatus];
  };

  useEffect(() => {
    setWidth(resizerWidth);
  }, [resizerWidth]);

  useEffect(() => {
    if (!campaignInfo.status) return;

    const setCallPromises = isSubCR
      ? [campaignServices.getSubCRWorkflow(id)]
      : [campaignServices.getCampaignRetry(id, 'workflows', version),
        campaignServices.chekCMDecisions(id, campaignInfo.version)];
    Promise.all(setCallPromises)
      .then(([res, statusCheking]) => {
        const wf = {
          ...res,
          statuses: addCollapsedDataWorkflow(res.statuses, t, timeZone),
          historicalStatuses: addCollapsedDataWorkflow(res.historicalStatuses || [], t, timeZone),
        };
        setWorkflow(wf);

        if (statusCheking) {
          setSubStatus(statusCheking.workflows);
        }
      }).catch(handleCriticalError);

    return () => { resetWF(); };
  }, refreshProgressBarDependencies());

  const updateWidths = (value, save = false) => {
    setResizer(value, save);
    setWidth(value);
  };

  const toggleStatus = () => {
    const newWidth = width > WF_STATUS_WIDTH.THRESHOLD ? WF_STATUS_WIDTH.MIN : WF_STATUS_WIDTH.OPEN;
    updateWidths(newWidth, true);
    setShowExtendedStatus(newWidth === WF_STATUS_WIDTH.OPEN);
  };

  const saveResizeValue = (value) => {
    updateWidths(value, true);
  };

  const isWFOpen = width > WF_STATUS_WIDTH.THRESHOLD;

  return (
    <div className={styles.Section} style={{ width: `${width}px` }}>
      <Resizer
        key={userData.configuration?.sizeSidebarLeft}
        range={{ min: WF_STATUS_WIDTH.MIN, max: WF_STATUS_WIDTH.MAX }}
        tooltipText={t('resizeX', { x: t('workflow.title') })}
        onResize={updateWidths}
        saveResizeValue={saveResizeValue}
      />
      <div id="history-title" className={`${styles.Title} ${!isWFOpen ? styles.Centered : ''}`}>
        <IcoButtonSimple id="btn-campaign-statis" icon="icon-diagramm-baum" handleClick={toggleStatus} selected={showExtendedStatus} />
        {isWFOpen && (
          <>
            <span>{t('workflow.title')}</span>
            <CloseButton onClick={toggleStatus} />
          </>
        )}
      </div>
      <StepsComponent
        collapsed={!isWFOpen}
        headerLanguage={headerLanguage}
        id={id}
        workflow={workflow}
        orientation="vertical"
        nonLinear
        subStatus={subStatus}
        campaignInfo={campaignInfo}
        isSubCR={isSubCR}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  userData: getUserAll(state),
  campaignInfo: getCampaignInfo(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  resizerWidth: getWFResizer(state),
  timeZone: getSettingsInfo(state)?.timezone,
  workflow: getWorkflow(state),
  recipients: getCampaignRecipients(state),
});

const actionCreators = {
  setResizer: setResizerWF,
  setWorkflow: setCampaignWorkflow,
  resetWF: resetWorkflow,
};

export default connect(mapStateToProps, actionCreators)(CampaignStatus);
